/* eslint-disable */
import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetUserPermissions } from "api/permission/getpermission";
import type { Permissions } from "api/permission/getpermission";

type PermissionContextValue = {
  permissions: Permissions | null;
  isLoading: boolean;
  isError: boolean;
};

const PermissionContext = createContext<PermissionContextValue | undefined>(
  undefined
);

export const PermissionProvider: React.FC<{
  userId?: string;
  children: React.ReactNode;
}> = ({ userId, children }) => {
  const queryClient = useQueryClient();

  const [cachedAuthUser, setCachedAuthUser] = useState<{ _id?: string }>(() => {
    return (queryClient.getQueryData(["auth-user"]) as { _id?: string }) ?? {};
  });

  const lastSeenIdRef = useRef<string | undefined>(cachedAuthUser?._id);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const u = queryClient.getQueryData(["auth-user"]) as
        | { _id?: string }
        | undefined;
      const newId = u?._id;

      if (lastSeenIdRef.current === newId) {
        return;
      }
      lastSeenIdRef.current = newId;
      setCachedAuthUser(u ?? {});
    });

    return unsubscribe;
  }, [queryClient]);

  const id = userId ?? cachedAuthUser?._id ?? "";
  const shouldFetch = Boolean(id);

  const { data, isLoading, isError } = useGetUserPermissions(id, {
    enabled: shouldFetch,
  });

  const permissions = useMemo(() => data?.data?.permissions ?? null, [data]);

  const isActuallyLoading = shouldFetch ? isLoading : false;

  return (
    <PermissionContext.Provider
      value={{ permissions, isLoading: isActuallyLoading, isError }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissionsContext = (): PermissionContextValue => {
  const ctx = useContext(PermissionContext);
  if (!ctx)
    throw new Error(
      "usePermissionsContext must be used within PermissionProvider"
    );
  return ctx;
};
